import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ArrowUp, AudioLines, Loader2, Mic, Paperclip, Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { MentionCombobox, type MentionItem } from './MentionCombobox'

interface Props {
  onSend: (content: string) => void
  onStop: () => void
  streaming: boolean
  mentionItems: MentionItem[]
  disabled?: boolean
  /** Files attached since the last message; with any, a message needs no text. */
  attachedCount?: number
  /** Absent where attaching files is not offered. */
  onUpload?: (files: File[]) => Promise<void>
  uploading?: boolean
  /** Starts talking to the assistant; absent where voice is not offered. */
  onVoice?: () => void
  voiceActive?: boolean
  /**
   * `bar` is the compact strip under a conversation (the widget). `card` is a
   * rounded box with its own toolbar, for the full page: large on the home,
   * smaller under a thread.
   */
  variant?: 'bar' | 'card'
  /** Shown inside the card above the text, e.g. the files going with the next message. */
  above?: ReactNode
  placeholder?: string
  /** Lines of text the box starts at; it grows as the text does. */
  rows?: number
  autoFocus?: boolean
  /** Text to put in the box, e.g. from a suggestion. A new `nonce` puts it in again. */
  prefill?: { text: string; nonce: number } | null
}

/** The `@` that opened the picker, or null when it is not open. */
function activeMention(value: string, caret: number): { start: number; query: string } | null {
  const before = value.slice(0, caret)
  const at = before.lastIndexOf('@')
  if (at === -1) return null
  // Only immediately after whitespace or at the start, so an email address
  // does not open the picker.
  if (at > 0 && !/\s/.test(before[at - 1] ?? '')) return null
  const query = before.slice(at + 1)
  if (/\s/.test(query)) return null
  return { start: at, query }
}

export function Composer({
  onSend,
  onStop,
  streaming,
  mentionItems,
  disabled,
  attachedCount = 0,
  onUpload,
  uploading,
  onVoice,
  voiceActive,
  variant = 'bar',
  above,
  placeholder = 'Ask Beacon, paste a screenshot, or use @ to reference a record.',
  rows = 2,
  autoFocus,
  prefill,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [value, setValue] = useState('')
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null)
  const ref = useRef<HTMLTextAreaElement>(null)

  // A suggestion fills the box and leaves the caret at the end, ready to finish.
  useEffect(() => {
    if (!prefill) return
    setValue(prefill.text)
    setMention(null)
    const box = ref.current
    if (!box) return
    box.focus()
    requestAnimationFrame(() => box.setSelectionRange(prefill.text.length, prefill.text.length))
  }, [prefill])

  const send = () => {
    // A screenshot alone is a question: what does this show?
    const content =
      value.trim() || (attachedCount > 0 ? 'Read the attached file and tell me what it shows.' : '')
    if (!content || streaming) return
    setValue('')
    setMention(null)
    onSend(content)
  }

  const insertMention = (item: MentionItem) => {
    if (!mention) return
    const caret = ref.current?.selectionStart ?? value.length
    // The label reads naturally; the id is appended so the agent has an
    // unambiguous handle without the operator typing a UUID.
    // The leading @ is kept deliberately: it delimits where the label starts, so
    // the server can parse the mention back out without swallowing the words in
    // front of it.
    const replacement = `@${item.label} (${item.kind}:${item.id}) `
    const next = value.slice(0, mention.start) + replacement + value.slice(caret)
    setValue(next)
    setMention(null)
    requestAnimationFrame(() => ref.current?.focus())
  }

  const takeFiles = async (files: FileList | null) => {
    const chosen = Array.from(files ?? [])
    if (chosen.length === 0 || !onUpload) return
    await onUpload(chosen)
  }

  const card = variant === 'card'
  const canSend = !disabled && (value.trim().length > 0 || attachedCount > 0)

  const fileInput = (
    <input
      ref={fileRef}
      type="file"
      multiple
      className="hidden"
      onChange={(event) => {
        void takeFiles(event.target.files)
        // Cleared so choosing the same file twice still fires a change.
        event.target.value = ''
      }}
    />
  )
  const attachButton = (
    <Button
      size={card ? 'icon-sm' : 'icon'}
      variant="ghost"
      disabled={!onUpload || uploading}
      onClick={() => fileRef.current?.click()}
      aria-label="Attach a file"
    >
      {uploading ? <Loader2 className="size-4 animate-spin" /> : <Paperclip className="size-4" />}
    </Button>
  )
  const textarea = (
    <Textarea
      ref={ref}
      value={value}
      disabled={disabled}
      rows={rows}
      autoFocus={autoFocus}
      placeholder={placeholder}
      // The box grows with its text; `rows` sets where it starts (1.5rem a line
      // at 16px, plus its padding), since field sizing ignores the attribute.
      style={card ? { minHeight: `${rows * 1.5 + 1.125}rem` } : undefined}
      className={
        card
          ? // 16px text: anything smaller makes iOS zoom the page when the box is tapped.
            'max-h-60 min-h-0 resize-none rounded-none border-0 bg-transparent px-4 pt-3.5 pb-1 text-lg shadow-none focus-visible:ring-0 dark:bg-transparent md:text-lg'
          : 'max-h-40 min-h-[2.5rem] resize-none'
      }
      onChange={(event) => {
        setValue(event.target.value)
        setMention(activeMention(event.target.value, event.target.selectionStart ?? 0))
      }}
      onPaste={(event) => {
        // A pasted screenshot arrives as a file on the clipboard, and is the
        // most common way an operator shares one.
        const pasted = Array.from(event.clipboardData.files)
        if (pasted.length > 0 && onUpload) {
          event.preventDefault()
          void onUpload(pasted)
        }
      }}
      onKeyDown={(event) => {
        // Enter sends; Shift+Enter is a newline. The picker intercepts
        // Enter first when it is open.
        if (event.key === 'Enter' && !event.shiftKey && !mention) {
          event.preventDefault()
          send()
        }
      }}
    />
  )
  const sendOrStop = streaming ? (
    <Button
      size={card ? 'icon-sm' : 'icon'}
      variant="outline"
      onClick={onStop}
      aria-label="Stop generating"
    >
      <Square className="size-4" />
    </Button>
  ) : (
    <Button
      size={card ? 'icon-sm' : 'icon'}
      onClick={send}
      disabled={!canSend}
      aria-label="Send message"
    >
      <ArrowUp className="size-4" />
    </Button>
  )

  return (
    <div
      className={cn(
        'relative',
        card
          ? 'rounded-2xl border border-border-strong bg-card shadow-sm transition-shadow focus-within:shadow-md'
          : 'border-t border-border bg-background p-3',
      )}
      onDragOver={(event) => {
        if (!onUpload) return
        event.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        if (!onUpload) return
        event.preventDefault()
        setDragging(false)
        void takeFiles(event.dataTransfer.files)
      }}
    >
      {dragging && (
        <div
          className={cn(
            'pointer-events-none absolute inset-2 z-10 flex items-center justify-center border-2 border-dashed border-primary bg-background/90 text-sm text-primary',
            card ? 'rounded-xl' : 'rounded-md',
          )}
        >
          Drop to attach
        </div>
      )}

      {mention && (
        <MentionCombobox
          items={mentionItems}
          query={mention.query}
          onSelect={insertMention}
          onDismiss={() => setMention(null)}
        />
      )}

      {card ? (
        <>
          {above}
          {fileInput}
          {textarea}
          <div className="flex items-center gap-1.5 px-2.5 pb-2.5">
            {attachButton}
            <div className="ml-auto flex items-center gap-1.5">
              {onVoice && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onVoice}
                  disabled={voiceActive || streaming}
                  aria-label="Talk to Beacon"
                  aria-pressed={voiceActive}
                >
                  <AudioLines />
                  Talk
                </Button>
              )}
              {sendOrStop}
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-end gap-2">
          {fileInput}
          {attachButton}
          {textarea}
          {onVoice && (
            <Button
              size="icon"
              variant={voiceActive ? 'secondary' : 'ghost'}
              onClick={onVoice}
              disabled={voiceActive || streaming}
              aria-label="Talk to Beacon"
              aria-pressed={voiceActive}
            >
              <Mic className="size-4" />
            </Button>
          )}
          {sendOrStop}
        </div>
      )}
    </div>
  )
}
